import * as uniqBy from 'lodash/uniqBy'
import { State } from 'interfaces/state'
import { Guards } from 'interfaces/guards'
import { connectLoadable, createParallelGuard } from 'helpers/loadableHelpers'
import { peergroupsGuard, selectedPeergroupTotalsGuard } from 'guards/globalGuards'
import { createSelectedTotalsByTypeSelector, selectedRelationSelector } from 'selectors/peergroupSelectors'
import { LineChartInnerOwnProps, LineChartInnerStateProps, RankChart, Tooltip, TooltipDirection } from 'components/features/pfm/LineChart/LineChart'
import LineChartLoadingOverlay from 'components/atoms/preloaders/LineChartLoadingOverlay'
import { createSelector } from 'reselect'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { findRank, percentileToItem, RANK_CHART_DIVIDERS, RANK_CHART_SECTIONS, RANK_CHART_TIPS, RANK_CHART_WIDTH } from 'helpers/trendHelpers'
import { createOwnPropsSelector } from 'selectors/globalSelectors'
import { memberMoneyflowTrendGuard } from 'guards/moneyflowGuards'
import { memberMoneyflowTrendsSelector } from 'selectors/moneyflowSelectors'
import { MoneyflowPage } from 'interfaces/pages'
import { Timeframe } from 'interfaces/chart'
import { CategoryId } from 'interfaces/meta'
import { formatRank, formatUSCurrency } from 'helpers/formatHelpers'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import { RANK_CHART_SCALES } from 'constants/chart'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { getTimeframeCopy } from 'helpers/categoriesHelpers'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'

export type AccountRankChartOwnProps = {
  type: MoneyflowPage
  timeframe: Timeframe
  categoryId: CategoryId
}

type OwnProps =
  & AccountRankChartOwnProps
  & LineChartInnerOwnProps

const getLineTitle = (type: MoneyflowPage, timeframe: Timeframe): string => {
  const w2 = type === 'spending' ? 'SPENDING' : 'INCOME'
  const w1 = timeframe === 'current-month' ?
    'PROJECTED' :
    (
      timeframe === 'last-month' ? 'YOUR' : 'YOUR AVG.'
    )

  return `${ w1 } ${ w2 }`
}

const createMapStateToProps = () => {
  const selector = createSelector(
    createSelectedTotalsByTypeSelector(),
    memberMoneyflowTrendsSelector,
    selectedRelationSelector,
    isMobileSelector,
    createOwnPropsSelector<OwnProps>(),
    withComparisonSelector,
    (totals, trend, selectedRelation, isMobile, ownProps, withComparison): LineChartInnerStateProps => {
      if (!totals) {
        throw createForgottenGuardError('MoneyflowRankChartContainer', 'selectedPeergroupTotalsGuard')
      }

      if (!selectedRelation) {
        throw createForgottenGuardError('MoneyflowRankChartContainer', 'peergroupsGuard')
      }

      if (!trend) {
        throw createForgottenGuardError('MoneyflowRankChartContainer', 'trendGuard')
      }


      const { peerValue: peerTotals, otherValue: otherTotals } = totals
      const { type, timeframe } = ownProps
      const rankCalculationValue = Math.round(trend.average)
      const peersRank = findRank(peerTotals.percentiles, rankCalculationValue)
      const othersRank = findRank(otherTotals.percentiles, rankCalculationValue)
      const lineTitle = getLineTitle(type, timeframe)

      // <= is important because when they squash to one that one should be up
      const peersTooltipDirection: TooltipDirection = peersRank <= othersRank ? 'up' : 'down'
      const othersTooltipDirection: TooltipDirection = peersTooltipDirection === 'up' ? 'down' : 'up'
      // squash tooltips if they have same amount
      const defaultTooltips: Tooltip[] = uniqBy<Tooltip>(
        [
          {
            type: 'peers',
            text: formatRank(peersRank),
            x: peersRank,
            y: rankCalculationValue,
            direction: peersTooltipDirection,
          },
          {
            type: 'others',
            text: formatRank(othersRank),
            x: othersRank,
            y: rankCalculationValue,
            direction: othersTooltipDirection,
          },
        ],
        (tooltip) => tooltip.x
      )

      return {
        memberItems: [],
        peersItems: peerTotals.percentiles.map(percentileToItem),
        otherItems: otherTotals.percentiles.map(percentileToItem),

        dots: [
          {
            x: peersRank,
            y: rankCalculationValue,
            type: 'peers',
            tooltipType: 'peers',
          },
          {
            x: othersRank,
            y: rankCalculationValue,
            type: 'others',
            tooltipType: 'others',
          },
        ],

        limitLine: {
          title: lineTitle,
          amount: rankCalculationValue,
          kind: 'member',
        },

        defaultTooltips,

        xWidth: RANK_CHART_WIDTH,

        dividers: RANK_CHART_DIVIDERS,
        tips: RANK_CHART_TIPS,
        headerTips: getTimeframeCopy(timeframe),
        sections: RANK_CHART_SECTIONS,

        scales: RANK_CHART_SCALES,
        valueDecorator: formatUSCurrency,

        selectedPeergroupName: selectedRelation.name,
        isMobile,
        withComparison,
      }
    }
  )

  return (state: State, ownProps: OwnProps): LineChartInnerStateProps => {
    const { categoryId } = ownProps

    /**
     * A back end doesn't support percentiles for categories
     * therefore we just push fake data to the control and don't show it (see `TrendRankBadge`)
     * But this will be supported so we'll wait. 🙏
     */
    if (categoryId !== ALL_CATEGORY_ID) {
      return null as any
    }

    return selector(state, ownProps)
  }
}

const trendRankBadgeGuards: Guards<OwnProps> = [
  peergroupsGuard,
  createParallelGuard(
    selectedPeergroupTotalsGuard,
    memberMoneyflowTrendGuard
  ),
]

const connected = connectLoadable<
  LineChartInnerStateProps,
  {},
  LineChartInnerOwnProps,
  OwnProps
>(trendRankBadgeGuards, LineChartLoadingOverlay)(createMapStateToProps)(RankChart)
export default connected
